import { http } from '@/lib/apiClient';
import type { CreateUtenteInput, Paginated, UUID, UpdateUtenteInput, UtenteConCliente, UtenteFilters } from '@/types';

const BASE = '/utenti';

/**
 * User management. `cliente_admin` sees only their own tenant thanks to the
 * `utenti_select`/`utenti_write` policies, so the frontend sends no tenant
 * filter of its own.
 */
export const utentiService = {
  list(filters: UtenteFilters = {}): Promise<Paginated<UtenteConCliente>> {
    return http.get<Paginated<UtenteConCliente>>(BASE, { params: filters });
  },

  get(id: UUID): Promise<UtenteConCliente> {
    return http.get<UtenteConCliente>(`${BASE}/${id}`);
  },

  /** Creates the user. Pass `invia_invito: false` to skip the activation e-mail. */
  invite(input: CreateUtenteInput): Promise<UtenteConCliente> {
    return http.post<UtenteConCliente, CreateUtenteInput>(BASE, input);
  },

  update(id: UUID, input: UpdateUtenteInput): Promise<UtenteConCliente> {
    return http.patch<UtenteConCliente, UpdateUtenteInput>(`${BASE}/${id}`, input);
  },

  /** Soft-disable: the user cannot sign in; tickets and comments stay as they are. */
  setAttivo(id: UUID, attivo: boolean): Promise<UtenteConCliente> {
    return http.patch<UtenteConCliente, { attivo: boolean }>(`${BASE}/${id}`, { attivo });
  },

  /** Hard-delete any user. Admins cannot delete themselves. */
  remove(id: UUID): Promise<void> {
    return http.delete(`${BASE}/${id}`);
  },

  resendInvite(id: UUID): Promise<void> {
    return http.post<void>(`${BASE}/${id}/resend-invite`, {});
  },

  attiva(id: UUID, clienteId?: UUID | null): Promise<UtenteConCliente> {
    return http.post<UtenteConCliente, { cliente_id?: UUID }>(`${BASE}/${id}/attiva`, {
      ...(clienteId ? { cliente_id: clienteId } : {}),
    });
  },

  sblocca(id: UUID): Promise<UtenteConCliente> {
    return http.post<UtenteConCliente>(`${BASE}/${id}/sblocca`);
  },

  resetMfa(id: UUID): Promise<UtenteConCliente> {
    return http.post<UtenteConCliente>(`${BASE}/${id}/reset-mfa`);
  },
};
